import React, { useEffect, useRef } from 'react';
import { ArrowRight, Shield, Cpu, Globe } from 'lucide-react';

const stats = [
  { num: 'West', accent: 'Africa', label: 'Primary Market Focus' },
  { num: 'End-', accent: 'to-End', label: 'Full Technology Stack' },
  { num: '3', accent: '-Phase', label: 'Transformation Approach' },
];

const badges = [
  { icon: <Globe size={14}/>, text: 'Ghana & West Africa' },
  { icon: <Shield size={14}/>, text: 'Enterprise-Grade Security' },
  { icon: <Cpu size={14}/>, text: 'Industrial IoT Ready' },
];

export default function Hero() {
  const gridRef = useRef(null);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!gridRef.current) return;
      const x = (e.clientX / window.innerWidth  - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      gridRef.current.style.transform = `translate(${x}px, ${y}px)`;
    };
    window.addEventListener('mousemove', onMouseMove);
    return () => window.removeEventListener('mousemove', onMouseMove);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" style={{
      minHeight: '100vh',
      display: 'flex', alignItems: 'center',
      padding: '120px 5% 80px',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Animated grid background */}
      <div ref={gridRef} style={{
        position: 'absolute', inset: '-40px',
        backgroundImage: `
          linear-gradient(rgba(0,180,216,0.05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,180,216,0.05) 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
        transition: 'transform 0.1s ease-out',
      }}/>

      {/* Glow orbs */}
      <div style={{
        position: 'absolute', top: '10%', right: '-5%',
        width: '550px', height: '550px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,180,216,0.07) 0%, transparent 70%)',
        pointerEvents: 'none',
      }}/>
      <div style={{
        position: 'absolute', bottom: '10%', left: '-10%',
        width: '400px', height: '400px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(244,166,35,0.04) 0%, transparent 70%)',
        pointerEvents: 'none',
      }}/>

      {/* Content */}
      <div style={{ position: 'relative', maxWidth: '780px' }}>

        {/* Eyebrow */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          background: 'rgba(0,180,216,0.08)',
          border: '1px solid rgba(0,180,216,0.25)',
          color: 'var(--electric)',
          padding: '0.35rem 0.9rem', borderRadius: '20px',
          fontSize: '0.75rem', fontWeight: 600, letterSpacing: '1.5px',
          textTransform: 'uppercase', marginBottom: '1.8rem',
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: 'var(--electric)',
            animation: 'pulse 2s infinite',
          }}/>
          Accra, Ghana — Serving West Africa & Beyond
        </div>

        {/* Headline */}
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'clamp(2.6rem, 5.5vw, 4.2rem)',
          fontWeight: 700, lineHeight: 1.1,
          color: 'var(--white)', letterSpacing: '-1.5px',
          marginBottom: '1.4rem',
        }}>
          Technology that powers<br />
          <span style={{ color: 'var(--electric)' }}>serious operations.</span>
        </h1>

        {/* Sub */}
        <p style={{
          fontSize: '1.1rem', color: 'var(--slate)',
          maxWidth: '600px', lineHeight: 1.75, marginBottom: '2.4rem',
        }}>
          ADELZONE LTD delivers enterprise IT infrastructure, network connectivity,
          and custom software architecture for companies operating in Ghana's most
          demanding industries — mining, oil & gas, energy, and beyond.
        </p>

        {/* CTA Buttons */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
          <button
            onClick={() => scrollTo('contact')}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              background: 'var(--electric)', color: 'var(--navy)',
              padding: '0.85rem 2rem', borderRadius: '8px',
              fontWeight: 700, fontSize: '0.92rem',
              transition: 'background 0.2s, transform 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background='var(--electric-hi)'; e.currentTarget.style.transform='translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='var(--electric)'; e.currentTarget.style.transform='translateY(0)'; }}>
            Start a Conversation <ArrowRight size={16}/>
          </button>
          <button
            onClick={() => scrollTo('services')}
            style={{
              background: 'transparent', color: 'var(--white)',
              padding: '0.85rem 2rem', borderRadius: '8px',
              fontWeight: 600, fontSize: '0.92rem',
              border: '1px solid rgba(255,255,255,0.2)',
              transition: 'border-color 0.2s, color 0.2s, transform 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor='var(--electric)'; e.currentTarget.style.color='var(--electric)'; e.currentTarget.style.transform='translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.2)'; e.currentTarget.style.color='var(--white)'; e.currentTarget.style.transform='translateY(0)'; }}>
            Our Services
          </button>
        </div>

        {/* Mini badges */}
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '3rem' }}>
          {badges.map((b, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              padding: '0.3rem 0.75rem', borderRadius: '20px',
              fontSize: '0.76rem', color: 'var(--slate-light)',
            }}>
              {b.icon} {b.text}
            </div>
          ))}
        </div>

        {/* Stats */}
        <div style={{
          display: 'flex', gap: '3rem', flexWrap: 'wrap',
          paddingTop: '2.5rem',
          borderTop: '1px solid rgba(0,180,216,0.12)',
        }}>
          {stats.map((s, i) => (
            <div key={i}>
              <div style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.9rem', fontWeight: 700,
                color: 'var(--white)', lineHeight: 1,
              }}>
                {s.num}<span style={{ color: 'var(--electric)' }}>{s.accent}</span>
              </div>
              <div style={{ fontSize: '0.76rem', color: 'var(--slate)', marginTop: '0.3rem', letterSpacing: '0.4px' }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%,100% { opacity:1; transform:scale(1); }
          50%      { opacity:0.3; transform:scale(1.4); }
        }
      `}</style>
    </section>
  );
}
